import React, { useEffect, useMemo, useRef, useState } from "react";
import { adsFieldValues } from "../../services/api";
import { ORIGIN_META, ORIGIN_ORDER } from "./FieldPicker";

/**
 * Filters built from the same field catalogue as the column picker.
 *
 * The panel used to be a fixed row of about twenty hand-written controls, so a
 * field could be shown as a column but not filtered on, and filters were not
 * grouped by where the value comes from. Now any field the catalogue marks
 * filterable can be added, under the same origin headings as the picker, with
 * an input chosen by its filter kind:
 *
 *   eq, array   pick one or more of the values actually stored
 *   contains    free text
 *   range       min and max
 *   date        from and to
 *   bool        yes or no
 *
 * `value` is {fieldKey: spec}, sent to the API unchanged as the `f` parameter.
 *
 * Clickable rows are divs, not <button>s: index.css gives every unclassed
 * button a dark fill, white text and a drop shadow, which turned the field
 * list into white-on-white pills.
 */

const inputStyle = {
  border: "1px solid #d1d5db", borderRadius: 8, padding: "6px 9px",
  fontSize: 13, fontWeight: 600, color: "#111827", background: "#fff", outline: "none",
  boxSizing: "border-box",
};

export function isEmptySpec(spec) {
  if (spec === undefined || spec === null || spec === "") return true;
  if (Array.isArray(spec)) return spec.length === 0;
  if (typeof spec === "object") {
    return Object.values(spec).every(v => v === undefined || v === null || v === "");
  }
  return false;
}

/** Drop empty entries, so an added-but-unfilled filter never reaches the API. */
export function compactFieldFilters(ff) {
  const out = {};
  for (const [k, v] of Object.entries(ff || {})) if (!isEmptySpec(v)) out[k] = v;
  return out;
}

/** A div that behaves like a button without inheriting the global button look. */
function Clickable({ onClick, style, children, title }) {
  return (
    <div
      role="button"
      tabIndex={0}
      title={title}
      onClick={onClick}
      onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick?.(e); } }}
      style={{ cursor: "pointer", userSelect: "none", ...style }}
    >
      {children}
    </div>
  );
}

function useOutsideClose(open, setOpen) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open, setOpen]);
  return ref;
}

const popover = {
  position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 700,
  background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12,
  boxShadow: "0 12px 32px rgba(15,23,42,0.16)",
};

function OriginHeading({ origin }) {
  const meta = ORIGIN_META[origin];
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 7, marginBottom: 5 }}>
      <span style={{ width: 7, height: 7, borderRadius: 999, background: meta.color, flexShrink: 0 }} />
      <span style={{ fontSize: 11.5, fontWeight: 800, color: meta.color }}>{meta.label}</span>
      <span style={{ fontSize: 10.5, color: "#9ca3af" }}>{meta.hint}</span>
    </div>
  );
}

function ValuePicker({ field, spec, onChange, brandId, source, includeCompetitors }) {
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState(null);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const ref = useOutsideClose(open, setOpen);
  const chosen = Array.isArray(spec) ? spec : (spec ? [spec] : []);

  // Loaded on first open, so a panel with many filters does not fire a request
  // per filter before anyone looks at a list.
  useEffect(() => {
    if (!open || values !== null) return;
    let cancelled = false;
    adsFieldValues(brandId, field.key, source, includeCompetitors)
      .then(d => { if (!cancelled) setValues(d.values || []); })
      .catch(e => { if (!cancelled) { setValues([]); setError(e?.message || "Could not load values."); } });
    return () => { cancelled = true; };
  }, [open, values, brandId, field.key, source, includeCompetitors]);

  const toggle = (v) => onChange(chosen.includes(v) ? chosen.filter(x => x !== v) : [...chosen, v]);

  const shown = (values || []).filter(v => !query || v.value.toLowerCase().includes(query.toLowerCase()));
  // The list holds the most common values only, so a typed value can be added.
  const typed = query.trim();
  const canAddTyped = typed && !(values || []).some(v => v.value.toLowerCase() === typed.toLowerCase());

  return (
    <div ref={ref} style={{ position: "relative", width: 200 }}>
      <Clickable
        onClick={() => setOpen(o => !o)}
        style={{ ...inputStyle, width: "100%", display: "flex", justifyContent: "space-between", gap: 8 }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: chosen.length ? "#111827" : "#9ca3af" }}>
          {chosen.length ? chosen.join(", ") : "Any"}
        </span>
        <span style={{ color: "#9ca3af" }}>▾</span>
      </Clickable>
      {open && (
        <div style={{ ...popover, top: "calc(100% + 4px)", width: 260, padding: 10 }}>
          <input
            autoFocus
            placeholder="Search, or type a value and press Enter"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && canAddTyped) { toggle(typed); setQuery(""); } }}
            style={{ ...inputStyle, width: "100%", fontWeight: 500, marginBottom: 8 }}
          />
          <div style={{ maxHeight: 240, overflowY: "auto", display: "grid", gap: 1 }}>
            {values === null && <div style={{ fontSize: 12, color: "#9ca3af", padding: 6 }}>Loading values…</div>}
            {error && <div style={{ fontSize: 12, color: "#b91c1c", padding: 6 }}>{error}</div>}
            {values !== null && !shown.length && !canAddTyped && (
              <div style={{ fontSize: 12, color: "#9ca3af", padding: 6 }}>No values stored for this field yet.</div>
            )}
            {canAddTyped && (
              <Clickable
                onClick={() => { toggle(typed); setQuery(""); }}
                style={{ padding: "5px 6px", borderRadius: 6, fontSize: 13, color: "#4338ca", fontWeight: 700 }}
              >
                + Add “{typed}”
              </Clickable>
            )}
            {shown.map(v => (
              <label key={v.value} style={{
                display: "flex", alignItems: "center", gap: 8, padding: "4px 6px", borderRadius: 6,
                fontSize: 13, color: "#111827", cursor: "pointer",
                background: chosen.includes(v.value) ? "#eef2ff" : "transparent",
              }}>
                <input type="checkbox" checked={chosen.includes(v.value)} onChange={() => toggle(v.value)} style={{ accentColor: "#6366f1" }} />
                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v.value}</span>
                {v.count != null && <span style={{ color: "#9ca3af", fontSize: 11.5, fontVariantNumeric: "tabular-nums" }}>{v.count}</span>}
              </label>
            ))}
            {chosen.filter(c => !(values || []).some(v => v.value === c)).map(c => (
              <label key={"typed-" + c} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 6px", borderRadius: 6, fontSize: 13, color: "#111827", cursor: "pointer", background: "#eef2ff" }}>
                <input type="checkbox" checked onChange={() => toggle(c)} style={{ accentColor: "#6366f1" }} />
                <span>{c}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function FilterInput({ field, spec, onChange, brandId, source, includeCompetitors }) {
  switch (field.filterable) {
    case "eq":
    case "array":
      return (
        <ValuePicker field={field} spec={spec} onChange={onChange}
                     brandId={brandId} source={source} includeCompetitors={includeCompetitors} />
      );
    case "contains":
      return (
        <input style={{ ...inputStyle, width: 200 }} placeholder="contains…"
               value={spec || ""} onChange={e => onChange(e.target.value)} />
      );
    case "range": {
      const s = spec || {};
      return (
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input type="number" style={{ ...inputStyle, width: 92 }} placeholder="min"
                 value={s.min ?? ""} onChange={e => onChange({ ...s, min: e.target.value === "" ? undefined : e.target.value })} />
          <span style={{ color: "#9ca3af" }}>to</span>
          <input type="number" style={{ ...inputStyle, width: 92 }} placeholder="max"
                 value={s.max ?? ""} onChange={e => onChange({ ...s, max: e.target.value === "" ? undefined : e.target.value })} />
        </div>
      );
    }
    case "date": {
      const s = spec || {};
      return (
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input type="date" style={inputStyle} value={s.from || ""}
                 onChange={e => onChange({ ...s, from: e.target.value || undefined })} />
          <span style={{ color: "#9ca3af" }}>to</span>
          <input type="date" style={inputStyle} value={s.to || ""}
                 onChange={e => onChange({ ...s, to: e.target.value || undefined })} />
        </div>
      );
    }
    case "bool":
      return (
        <select style={{ ...inputStyle, width: 110 }}
                value={spec === true ? "true" : spec === false ? "false" : ""}
                onChange={e => onChange(e.target.value === "" ? undefined : e.target.value === "true")}>
          <option value="">Any</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      );
    default:
      return null;
  }
}

/** One active filter: label, where it comes from, its input, and a remove. */
function FilterCard({ field, spec, onChange, onRemove, brandId, source, includeCompetitors }) {
  const meta = ORIGIN_META[field.origin] || ORIGIN_META.api;
  return (
    <div style={{
      background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10,
      padding: "8px 10px 10px", display: "grid", gap: 6,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
        <span style={{ width: 7, height: 7, borderRadius: 999, background: meta.color, flexShrink: 0 }} title={meta.label} />
        <span title={field.note || field.key} style={{ fontSize: 12.5, fontWeight: 800, color: "#111827" }}>
          {field.label}
        </span>
        <span style={{ fontSize: 11, color: "#9ca3af" }}>{field.group}</span>
        <div style={{ flex: 1 }} />
        <Clickable
          onClick={onRemove}
          title="Remove this filter"
          style={{ color: "#9ca3af", fontSize: 16, lineHeight: 1, padding: "0 2px" }}
        >
          ×
        </Clickable>
      </div>
      <FilterInput field={field} spec={spec} onChange={onChange}
                   brandId={brandId} source={source} includeCompetitors={includeCompetitors} />
    </div>
  );
}

export default function FieldFilterBuilder({
  catalog = [], value = {}, onChange, brandId, source, includeCompetitors = false,
}) {
  const [adding, setAdding] = useState(false);
  const [query, setQuery] = useState("");
  const addRef = useOutsideClose(adding, setAdding);

  const filterable = useMemo(
    () => catalog.filter(f => f.filterable && (!source || (f.providers || []).includes(source))),
    [catalog, source],
  );
  const byKey = useMemo(() => Object.fromEntries(filterable.map(f => [f.key, f])), [filterable]);

  // Rows added but not filled in yet still show, so they can be completed.
  const activeKeys = Object.keys(value || {}).filter(k => byKey[k]);

  const setSpec = (key, spec) => onChange({ ...(value || {}), [key]: spec });
  const remove = (key) => {
    const next = { ...(value || {}) };
    delete next[key];
    onChange(next);
  };
  const add = (key) => {
    if (!(key in (value || {}))) onChange({ ...(value || {}), [key]: undefined });
    setAdding(false);
    setQuery("");
  };

  const q = query.trim().toLowerCase();
  const addable = filterable
    .filter(f => !activeKeys.includes(f.key))
    .filter(f => !q || f.label.toLowerCase().includes(q) || f.group.toLowerCase().includes(q));
  const addableByOrigin = {};
  for (const f of addable) (addableByOrigin[f.origin] = addableByOrigin[f.origin] || []).push(f);

  // Active filters in origin order, so they read in the same sequence as the menu.
  const active = activeKeys
    .map(k => byKey[k])
    .sort((a, b) => ORIGIN_ORDER.indexOf(a.origin) - ORIGIN_ORDER.indexOf(b.origin));

  return (
    <div style={{ width: "100%", display: "grid", gap: 10 }}>
      {active.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))", gap: 10 }}>
          {active.map(f => (
            <FilterCard
              key={f.key}
              field={f}
              spec={value[f.key]}
              onChange={s => setSpec(f.key, s)}
              onRemove={() => remove(f.key)}
              brandId={brandId}
              source={source}
              includeCompetitors={includeCompetitors}
            />
          ))}
        </div>
      )}

      <div ref={addRef} style={{ position: "relative" }}>
        <Clickable
          onClick={() => setAdding(a => !a)}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            border: "1px dashed #a5b4fc", background: adding ? "#eef2ff" : "#fff", color: "#4338ca",
            borderRadius: 8, padding: "7px 12px", fontSize: 13, fontWeight: 800,
          }}
        >
          + Add filter
        </Clickable>

        {adding && (
          <div style={{ ...popover, width: 380, padding: 14 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
              <strong style={{ fontSize: 13.5, color: "#111827" }}>Add a filter</strong>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>
                {filterable.length} fields
              </span>
            </div>
            <p style={{ margin: "6px 0 10px", fontSize: 11.5, color: "#6b7280", lineHeight: 1.45 }}>
              Same fields and grouping as the Fields picker. Pick one to add it, then set its value.
            </p>
            <input
              autoFocus
              placeholder="Search fields…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              style={{ ...inputStyle, width: "100%", fontWeight: 500, marginBottom: 10 }}
            />
            <div style={{ maxHeight: 380, overflowY: "auto", paddingRight: 2 }}>
              {ORIGIN_ORDER.filter(o => (addableByOrigin[o] || []).length).map(origin => (
                <div key={origin} style={{ marginBottom: 12 }}>
                  <OriginHeading origin={origin} />
                  <div style={{ display: "grid", gap: 1 }}>
                    {addableByOrigin[origin].map(f => (
                      <Clickable
                        key={f.key}
                        onClick={() => add(f.key)}
                        title={f.note || f.key}
                        style={{ padding: "4px 6px 4px 20px", borderRadius: 6, fontSize: 13, color: "#111827", lineHeight: 1.35 }}
                      >
                        <div
                          onMouseEnter={e => { e.currentTarget.parentElement.style.background = "#f1f5f9"; }}
                          onMouseLeave={e => { e.currentTarget.parentElement.style.background = "transparent"; }}
                        >
                          {f.label}
                          <span style={{ color: "#9ca3af", fontSize: 11, display: "block" }}>{f.group}</span>
                        </div>
                      </Clickable>
                    ))}
                  </div>
                </div>
              ))}
              {!addable.length && (
                <div style={{ fontSize: 12.5, color: "#9ca3af", padding: "10px 2px" }}>
                  {q ? `No fields match “${query}”.` : "Every field already has a filter."}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
